import appConstants, { SVG_ICON_NAME } from '../appConstants';

// Constants - extracted to avoid recreation on each function call
const DAY_NAMES = [
  appConstants.DOW.SUNDAY.value,
  appConstants.DOW.MONDAY.value,
  appConstants.DOW.TUESDAY.value,
  appConstants.DOW.WEDNESDAY.value,
  appConstants.DOW.THURSDAY.value,
  appConstants.DOW.FRIDAY.value,
  appConstants.DOW.SATURDAY.value
];

const DAY_MAP = {
  [appConstants.DOW.MONDAY.value]: appConstants.DOW.MONDAY,
  [appConstants.DOW.TUESDAY.value]: appConstants.DOW.TUESDAY,
  [appConstants.DOW.WEDNESDAY.value]: appConstants.DOW.WEDNESDAY,
  [appConstants.DOW.THURSDAY.value]: appConstants.DOW.THURSDAY,
  [appConstants.DOW.FRIDAY.value]: appConstants.DOW.FRIDAY,
  [appConstants.DOW.SATURDAY.value]: appConstants.DOW.SATURDAY,
  [appConstants.DOW.SUNDAY.value]: appConstants.DOW.SUNDAY
};

// Helper functions
const getDayData = (dayValue) => DAY_MAP[dayValue] || null;

const getDayOrder = (dayValue) => {
  const dayData = getDayData(dayValue);
  return dayData?.order ?? 0;
};

const isValidDaysArray = (deliveryDowOpen) => 
  Array.isArray(deliveryDowOpen) && deliveryDowOpen.length > 0;

// Internal helper functions
const getCurrentDayOfWeek = (planDate) => {
  const date = planDate ? new Date(planDate) : new Date();
  return DAY_NAMES[date.getUTCDay()];
};

const getNextEligibleDeliveryDay = (deliveryDowOpen, planDate) => {
  if (!isValidDaysArray(deliveryDowOpen)) return null;

  const currentDayOrder = getDayOrder(getCurrentDayOfWeek(planDate));
  
  // Sort eligible days by order
  const sortedDays = deliveryDowOpen
    .map(day => ({ day, order: getDayOrder(day) }))
    .sort((a, b) => a.order - b.order);

  // Find first day after current day
  const nextDay = sortedDays.find(({ order }) => order > currentDayOrder);
  
  // Return next day or wrap to first day of week
  return nextDay ? nextDay.day : sortedDays[0]?.day ?? null;
};

const getDowIcon = (day) => getDayData(day)?.icon ?? '';

const getEligibleDaysTooltip = (deliveryDowOpen, planDate) => {
  if (!isValidDaysArray(deliveryDowOpen)) {
    return 'No delivery days available';
  }

  const currentDayOrder = getDayOrder(getCurrentDayOfWeek(planDate));

  // Sort days relative to current day and get characters in one pass
  const sortedDayCharacters = deliveryDowOpen
    .map(day => {
      const order = getDayOrder(day);
      return {
        day,
        adjustedOrder: order >= currentDayOrder ? order : order + 7
      };
    })
    .sort((a, b) => a.adjustedOrder - b.adjustedOrder)
    .map(({ day }) => getDowIcon(day));

  return `Day Restriction: ${sortedDayCharacters.join(', ')}`;
};

const isCurrentDayEligible = (deliveryDowOpen, planDate) => {
  if (!isValidDaysArray(deliveryDowOpen)) return false;
  return deliveryDowOpen.includes(getCurrentDayOfWeek(planDate));
};

export const createDowSpecialNeeds = (deliveryDowOpen, planDate) => {
  if (!isValidDaysArray(deliveryDowOpen)) return null;
  if (isCurrentDayEligible(deliveryDowOpen, planDate)) return null;

  const nextEligibleDay = getNextEligibleDeliveryDay(deliveryDowOpen, planDate);
  if (!nextEligibleDay) return null;

  return {
    value: `DOW`,
    text: getDowIcon(nextEligibleDay),
    icon: `dow-${nextEligibleDay}`,
    tooltip: getEligibleDaysTooltip(deliveryDowOpen, planDate),
    isSvgIcon:true,
    svgIcon: SVG_ICON_NAME.DOW
  };
};
